"use client";

import { useMemo, useState } from "react";
import { passiveVoiceStats } from "@/lib/passiveVoice";
import { useI18n } from "@/components/providers/I18nProvider";
import { uiIntlLocale } from "@/lib/i18n/languages";

const SAMPLE = `The window was broken by the storm. Rain was driven through the gap, and by morning the carpet had been ruined.

Nel opened the door. She had not been told about the leak, and the landlord could not be reached. The repair was finally made three weeks later, by a man who was sent from the agency and who was clearly being paid by the hour.`;

/**
 * The checker itself. Everything runs in the browser — nothing is uploaded,
 * which is both the honest answer to "where does my manuscript go" and the
 * reason this can be free and instant.
 */
export function PassiveVoiceChecker() {
  const { locale, t } = useI18n();
  const [text, setText] = useState("");

  const stats = useMemo(() => passiveVoiceStats(text), [text]);

  /** The input split into plain and highlighted runs, in order. */
  const segments = useMemo(() => {
    if (!text) return [];
    const out: { text: string; passive: boolean }[] = [];
    let cursor = 0;
    for (const m of stats.matches) {
      if (m.index > cursor) out.push({ text: text.slice(cursor, m.index), passive: false });
      out.push({ text: m.text, passive: true });
      cursor = m.index + m.text.length;
    }
    if (cursor < text.length) out.push({ text: text.slice(cursor), passive: false });
    return out;
  }, [text, stats.matches]);

  const pct = Math.round(stats.ratio * 100);
  const numberLocale = uiIntlLocale(locale);
  const formatNumber = (value: number) => value.toLocaleString(numberLocale);
  const sentenceLabel = stats.sentences === 1 ? "sentence" : "sentences";

  /**
   * Guidance, not a grade. Ranges are deliberately wide and the top band still
   * says the passive may be correct — a checker that demands zero passive
   * makes prose worse.
   */
  const verdict = !stats.sentences
    ? null
    : pct <= 5
      ? { label: "Barely any passive voice", tone: "good" as const }
      : pct <= 15
        ? { label: "Normal range for fiction", tone: "good" as const }
        : pct <= 30
          ? { label: "Worth a look", tone: "warn" as const }
          : { label: "Heavy — most sentences hide who acts", tone: "warn" as const };

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label htmlFor="pv-input" className="text-sm font-medium">
          {t("marketing.checker.pasteWriting")}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setText(SAMPLE)}
            className="min-h-[36px] rounded-lg border border-border px-3 text-sm hover:bg-muted"
          >
            {t("marketing.checker.trySample")}
          </button>
          <button
            type="button"
            onClick={() => setText("")}
            disabled={!text}
            className="min-h-[36px] rounded-lg border border-border px-3 text-sm hover:bg-muted disabled:opacity-40"
          >
            {t("marketing.checker.clear")}
          </button>
        </div>
      </div>

      <textarea
        id="pv-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        spellCheck={false}
        placeholder={t("marketing.checker.placeholder")}
        className="mt-2 w-full resize-y rounded-xl border border-border bg-background p-4 text-sm leading-relaxed"
      />

      {stats.sentences > 0 && (
        <>
          <div
            data-testid="pv-summary"
            className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm"
          >
            <span className="tabular-nums">
              {t("marketing.passive.summary", {
                passive: formatNumber(stats.passiveSentences),
                sentences: formatNumber(stats.sentences),
                sentenceLabel: locale === "ar"
                  ? stats.sentences === 1
                    ? "جملة"
                    : stats.sentences === 2
                      ? "جملتين"
                      : "جمل"
                  : sentenceLabel,
              })}
            </span>
            <span className="tabular-nums text-muted-foreground">{formatNumber(pct)}{t("marketing.beat.percent")}</span>
            <span className="tabular-nums text-muted-foreground">
              {formatNumber(stats.words)} {t("marketing.passive.words")}
            </span>
            {verdict && (
              <span
                className={
                  verdict.tone === "good"
                    ? "rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400"
                    : "rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-500"
                }
              >
                {t(
                  verdict.label === "Barely any passive voice"
                    ? "marketing.passive.barelyAny"
                    : verdict.label === "Normal range for fiction"
                      ? "marketing.passive.normalRange"
                      : verdict.label === "Worth a look"
                        ? "marketing.passive.worthALook"
                        : "marketing.passive.heavy",
                )}
              </span>
            )}
          </div>

          <div
            data-testid="pv-output"
            className="mt-4 whitespace-pre-wrap rounded-xl border border-border p-4 text-sm leading-relaxed"
          >
            {segments.map((seg, i) =>
              seg.passive ? (
                <mark
                  key={i}
                  className="rounded bg-amber-400/30 px-0.5 text-inherit underline decoration-amber-600 decoration-wavy underline-offset-4"
                >
                  {seg.text}
                </mark>
              ) : (
                <span key={i}>{seg.text}</span>
              ),
            )}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {t("marketing.passive.guidance")}
          </p>
        </>
      )}
    </div>
  );
}
